/**
 * Parser registry — the engine's dispatch table.
 *
 * Parsers are plain objects, not subclasses: there is no base class to keep in
 * sync as SVG, Figma, Canva, PSD, and PDF arrive. A parser registers itself and
 * the registry picks the highest-priority one that claims the input.
 *
 * This module is the whole reason a future parser needs no change to existing
 * code. Nothing here knows what a JSON file or an SVG file looks like.
 */

/**
 * Registration order is preserved, because it is the documented tie-breaker
 * when two parsers declare the same priority.
 */
let parsers = [];

/**
 * Register a parser, replacing any parser already holding the same `id`.
 *
 * Replacement rather than append is what keeps a dev-server hot reload from
 * accumulating three stale copies of the same parser, each shadowing the last.
 * The replacement keeps the original slot, so re-registering never silently
 * reshuffles tie-breaking against parsers registered after it.
 *
 * @param {import('./types').Parser} parser
 */
export const register = (parser) => {
  if (!parser || typeof parser !== 'object') {
    throw new TypeError('register expects a parser object');
  }
  if (!parser.id) throw new TypeError('a parser needs an id');
  if (typeof parser.canParse !== 'function') throw new TypeError(`parser "${parser.id}" needs canParse()`);
  if (typeof parser.parse !== 'function') throw new TypeError(`parser "${parser.id}" needs parse()`);

  const at = parsers.findIndex((p) => p.id === parser.id);
  if (at === -1) parsers.push(parser);
  else parsers[at] = parser;
};

/**
 * Remove a parser by id.
 *
 * @param {string} id
 * @returns {boolean} whether anything was removed
 */
export const unregister = (id) => {
  const before = parsers.length;
  parsers = parsers.filter((p) => p.id !== id);
  return parsers.length !== before;
};

/**
 * Every registered parser, highest priority first.
 *
 * Returns a copy: a caller that sorts, splices, or empties the result must not
 * be able to corrupt dispatch for everyone else. `Array.prototype.sort` is
 * stable, so equal priorities keep registration order — earliest first.
 *
 * @returns {import('./types').Parser[]}
 */
export const getAll = () =>
  [...parsers].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));

/**
 * The highest-priority parser that claims this input, or `null`.
 *
 * `canParse` runs inside try/catch: a parser that throws while sniffing is
 * skipped, not allowed to break dispatch for the parsers behind it. A broken
 * PSD sniffer must never cost you your JSON import.
 *
 * @param {import('./types').Input} input
 * @returns {import('./types').Parser|null}
 */
export const getParser = (input) => {
  for (const parser of getAll()) {
    try {
      if (parser.canParse(input)) return parser;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[templateEngine] parser "${parser.id}" threw during canParse; skipping it`, err);
    }
  }
  return null;
};

/** Drop every registration. Test seam — production code registers once at import. */
export const clear = () => {
  parsers = [];
};

/** The singleton the public API exposes. */
export const parserRegistry = { register, unregister, getAll, getParser, clear };
